import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  OnDestroy,
  effect,
  inject,
  input,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

/**
 * カードグリッド向けの自己完結型ビデオプレイヤー。
 * MKPlayer インスタンスを input で受け取り、カスタムオーバーレイUIを表示する。
 */
@Component({
  selector: 'app-media-video-player',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, DecimalPipe],
  templateUrl: './media-video-player.component.html',
  styleUrls: ['./media-video-player.component.scss'],
  host: { '(click)': 'onPlayerClick($event)' },
})
export class MediaVideoPlayerComponent implements OnDestroy {
  readonly playerInstance = input<any>(null);
  readonly thumbnailUrl = input<string>('');

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostEl = inject(ElementRef);

  videoPlaying = false;
  videoMuted = true;
  videoCurrentTime = 0;
  videoDuration = 0;
  videoPlaybackRate = 1;
  dragging = false;
  replaySpin = false;

  /** Logical "play requested, not yet confirmed playing" flag (blocks duplicate play/seek). */
  playRequestPending = false;
  /** Visual startup spinner flag — set only after a short debounce so fast starts never flash. */
  showPlaybackStarting = false;
  /**
   * One-way readiness latch. Becomes true once the current player has proven playable
   * (duration > 0 / canplay / playing). Reset only when the playerInstance reference changes,
   * so transient DRM/polling glitches never flip the UI back to "loading".
   */
  readyLatched = false;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private playStartTimer: ReturnType<typeof setTimeout> | null = null;
  private playStartDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Incremented on every playerInstance reference change; guards stale async callbacks. */
  private playerGeneration = 0;
  private dragRatio = 0;
  private dragStartRatio = 0;
  private dragged = false;
  private progressRailEl?: HTMLElement;
  private activeTouchId: number | null = null;
  private wasPlayingBeforeDrag = false;
  private lastPreviewTs = 0;
  private readonly previewThrottleMs = 40;
  private readonly dragThreshold = 0.02;
  private wasEnded = false;
  private spinTimer: ReturnType<typeof setTimeout> | null = null;
  /** Suppress polling overwrite after play/seek for a short grace period */
  private seekingUntil = 0;
  private currentPlayer: any = null;
  private hasPlaybackSession = false;
  private thumbnailLoadFailed = false;
  private boundVideoElement: HTMLVideoElement | null = null;
  private videoEventCleanup: Array<() => void> = [];
  private readonly playStartTimeoutMs = 8_000;
  private readonly playStartDebounceMs = 200;

  private boundMove = (ev: PointerEvent) => this.onPointerMove(ev);
  private boundUp = (ev: PointerEvent) => this.onPointerUp(ev);
  private boundTouchMove = (ev: TouchEvent) => this.onTouchMove(ev);
  private boundTouchEnd = (ev: TouchEvent) => this.onTouchEnd(ev);

  constructor() {
    effect(() => {
      const player = this.playerInstance();
      if (player !== this.currentPlayer) {
        this.currentPlayer = player;
        this.playerGeneration++;
        this.resetPlaybackViewState();
      }
      if (player) {
        this.startPolling(player);
      } else {
        this.stopPolling();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.detachDragListeners();
    this.unbindVideoElementEvents();
    if (this.spinTimer) clearTimeout(this.spinTimer);
    this.endPlaybackStartWait();
  }

  get progressPercent(): number {
    if (!this.videoDuration || this.videoDuration <= 0) return 0;
    if (this.dragging) return this.dragRatio * 100;
    return (this.videoCurrentTime / this.videoDuration) * 100;
  }

  get isEnded(): boolean {
    return (
      Number.isFinite(this.videoDuration) &&
      this.videoDuration > 0 &&
      Number.isFinite(this.videoCurrentTime) &&
      !this.videoPlaying &&
      this.videoCurrentTime >= this.videoDuration - 0.5
    );
  }

  /**
   * 明示的なビュー相。スピナー/サムネ/操作可否はすべてこれを基準に決める。
   * - cold    : プレイヤー未生成（グリッドのオフスクリーン等）→ サムネのみ、スピナー無し
   * - loading : プレイヤーは在るが未だ再生可能でない → 中央ローディングスピナー、操作不可
   * - starting: 再生要求中（デバウンス後）→ 再生ボタンにスピナー
   * - playing : 再生中
   * - ended   : 末尾到達
   * - ready   : 再生可能・停止中
   */
  get viewPhase(): 'cold' | 'loading' | 'starting' | 'playing' | 'ended' | 'ready' {
    if (!this.playerInstance()) return 'cold';
    if (!this.readyLatched) return 'loading';
    if (this.showPlaybackStarting) return 'starting';
    if (this.videoPlaying) return 'playing';
    if (this.isEnded) return 'ended';
    return 'ready';
  }

  /** 中央ローディングスピナー（プレイヤーは在るが未準備）。 */
  get showLoadingSpinner(): boolean {
    return this.viewPhase === 'loading';
  }

  /** 操作（再生/シーク/ドラッグ）を無効化すべきか。 */
  get controlsDisabled(): boolean {
    const phase = this.viewPhase;
    return phase === 'cold' || phase === 'loading' || this.playRequestPending;
  }

  get showThumbnail(): boolean {
    if (!this.thumbnailUrl() || this.thumbnailLoadFailed) return false;
    const phase = this.viewPhase;
    return phase === 'cold' || phase === 'loading' || phase === 'ready' || phase === 'starting';
  }

  onThumbnailError(): void {
    this.thumbnailLoadFailed = true;
    this.cdr.markForCheck();
  }

  formatTime(sec: number): string {
    if (!Number.isFinite(sec) || sec < 0) return '0:00';
    const total = Math.floor(sec);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${('0' + s).slice(-2)}`;
  }

  // --- Controls ---

  onPlayerClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.controlsDisabled) return;

    const target = event.target as HTMLElement | null;
    if (target?.closest('button, .progress-rail')) return;

    this.togglePlay();
  }

  togglePlay(): void {
    const player = this.playerInstance();
    if (!player || this.controlsDisabled) return;
    try {
      if (this.isEnded) {
        this.videoCurrentTime = 0;
        this.wasEnded = false;
        this.beginPlaybackStartWait();
        this.seekingUntil = performance.now() + 500;
        player.seek?.(0);
        this.requestPlay(player);
      } else if (this.videoPlaying) {
        // Explicit user pause — safe to clear any pending startup state.
        this.videoPlaying = false;
        this.endPlaybackStartWait();
        player.pause?.();
      } else {
        this.beginPlaybackStartWait();
        this.seekingUntil = performance.now() + 300;
        this.requestPlay(player);
      }
    } catch {
      this.endPlaybackStartWait();
    }
    this.cdr.markForCheck();
  }

  /** play() を呼び、拒否時は現世代のみ起動待ちを解除する（古いプレイヤーの拒否で新状態を壊さない）。 */
  private requestPlay(player: any): void {
    const gen = this.playerGeneration;
    const result = player.play?.();
    Promise.resolve(result).catch(() => {
      if (gen === this.playerGeneration) this.endPlaybackStartWait();
    });
  }

  seekRelative(delta: number): void {
    const player = this.playerInstance();
    if (!player || !this.videoDuration || this.controlsDisabled) return;
    const target = Math.max(0, Math.min(this.videoCurrentTime + delta, this.videoDuration));
    try {
      player.seek?.(target);
    } catch { /* noop */ }
    this.videoCurrentTime = target;
    this.seekingUntil = performance.now() + 300;
    this.cdr.markForCheck();
  }

  toggleMute(): void {
    const player = this.playerInstance();
    if (!player) return;
    this.videoMuted = !this.videoMuted;
    try {
      if (this.videoMuted) {
        player.mute?.();
        player.setVolume?.(0);
      } else {
        player.unmute?.();
        player.setVolume?.(1);
      }
    } catch {
      const videoEl = this.getVideoElement();
      if (videoEl) videoEl.muted = this.videoMuted;
    }
  }

  cycleRate(): void {
    const rates = [1, 1.25, 1.5, 2];
    const idx = rates.findIndex((r) => Math.abs(r - this.videoPlaybackRate) < 0.01);
    this.videoPlaybackRate = rates[(idx + 1) % rates.length];
    try {
      this.playerInstance()?.setPlaybackSpeed?.(this.videoPlaybackRate);
    } catch { /* noop */ }
    const videoEl = this.getVideoElement();
    if (videoEl) videoEl.playbackRate = this.videoPlaybackRate;
  }

  toggleFullscreen(): void {
    const el = this.hostEl.nativeElement as HTMLElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }

  // --- Progress bar drag (pointer + touch) ---

  onRailPointerDown(event: PointerEvent): void {
    if (!this.videoDuration || this.videoDuration <= 0 || this.controlsDisabled) return;
    event.preventDefault();
    event.stopPropagation();
    const rail = event.currentTarget as HTMLElement;
    if (!rail) return;
    this.startDrag(event.clientX, rail);
    window.addEventListener('pointermove', this.boundMove);
    window.addEventListener('pointerup', this.boundUp);
    window.addEventListener('pointercancel', this.boundUp);
  }

  onRailTouchStart(event: TouchEvent): void {
    if (!this.videoDuration || this.videoDuration <= 0 || this.controlsDisabled) return;
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    event.preventDefault();
    event.stopPropagation();
    const rail = event.currentTarget as HTMLElement;
    if (!rail) return;
    this.activeTouchId = touch.identifier;
    this.startDrag(touch.clientX, rail);
    window.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    window.addEventListener('touchend', this.boundTouchEnd);
    window.addEventListener('touchcancel', this.boundTouchEnd);
  }

  // --- Private: Polling ---

  private startPolling(player: any): void {
    this.stopPolling();
    const poll = () => {
      // Suppress polling updates during drag to prevent progress bar jitter
      if (this.dragging) return;
      // Parent may already have replaced this player. Stop stale timer immediately.
      if (this.playerInstance() !== player) {
        this.stopPolling();
        return;
      }

      const now = performance.now();
      const seeking = now < this.seekingUntil;

      const videoEl = this.getVideoElement();
      if (videoEl) {
        this.bindVideoElementEvents(videoEl);
      }
      let current = Number(videoEl?.currentTime ?? 0);
      let duration = Number(videoEl?.duration ?? 0);
      let playerPlaying = !(videoEl?.paused ?? true);
      let playerMuted = !!videoEl?.muted;

      try {
        current = Number(player.getCurrentTime?.() ?? current);
      } catch (error) {
        if (this.handleDestroyedPlayerError(error, player)) return;
      }
      try {
        duration = Number(player.getDuration?.() ?? duration);
      } catch (error) {
        if (this.handleDestroyedPlayerError(error, player)) return;
      }
      try {
        const apiPlaying = player.isPlaying?.();
        if (apiPlaying != null) {
          playerPlaying = !!apiPlaying || playerPlaying;
        }
      } catch (error) {
        if (this.handleDestroyedPlayerError(error, player)) return;
      }
      try {
        playerMuted = !!(player.isMuted?.() ?? playerMuted);
      } catch (error) {
        if (this.handleDestroyedPlayerError(error, player)) return;
      }

      // During seeking grace period, only accept player values that confirm
      // the operation completed (time moved away from end, or isPlaying is true)
      if (seeking) {
        if (playerPlaying) {
          // Player confirmed playing — end grace period early
          this.seekingUntil = 0;
          this.endPlaybackStartWait();
          if (Number.isFinite(current)) this.videoCurrentTime = current;
          this.videoPlaying = true;
        }
        // Otherwise keep local values (set by togglePlay/seekRelative)
      } else {
        if (Number.isFinite(current)) this.videoCurrentTime = current;
        this.videoPlaying = playerPlaying;
      }

      if (playerPlaying || current > 0) {
        this.hasPlaybackSession = true;
      }

      if (Number.isFinite(duration) && duration > 0) this.videoDuration = duration;

      // Confirmed playing (API or native) clears any startup spinner and latches ready.
      if (playerPlaying) {
        this.endPlaybackStartWait();
        this.markReady();
      }
      // Latch readiness from any playable signal. Never unlatch on a later poll that
      // momentarily reports duration 0/NaN or readyState drops (DRM startup glitches).
      if ((Number.isFinite(duration) && duration > 0) || (videoEl?.readyState ?? 0) >= 2) {
        this.markReady();
      }

      // Track mute state from player
      this.videoMuted = playerMuted;

      // Detect end-of-video transition for replay spin animation
      const ended = this.isEnded;
      if (ended && !this.wasEnded) {
        this.triggerReplaySpin();
      }
      this.wasEnded = ended;

      this.cdr.markForCheck();
    };
    poll();
    this.pollTimer = setInterval(poll, 80);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private getVideoElement(): HTMLVideoElement | null {
    return this.hostEl.nativeElement.querySelector('video') ?? null;
  }

  private bindVideoElementEvents(videoEl: HTMLVideoElement): void {
    if (this.boundVideoElement === videoEl) return;
    this.unbindVideoElementEvents();
    this.boundVideoElement = videoEl;

    const onPlaying = () => {
      this.hasPlaybackSession = true;
      this.videoPlaying = true;
      this.markReady();
      this.endPlaybackStartWait();
      this.cdr.markForCheck();
    };
    const onPause = () => {
      // Reflect paused state, but do NOT clear a pending startup here: DRM startup can
      // emit a transient `pause` before `playing`. Pending is cleared on confirmed play,
      // explicit user pause, the 8s timeout, or a player change.
      this.videoPlaying = false;
      this.cdr.markForCheck();
    };
    const onDurationChange = () => {
      if (Number.isFinite(videoEl.duration) && videoEl.duration > 0) {
        this.videoDuration = videoEl.duration;
        this.markReady();
        this.cdr.markForCheck();
      }
    };
    const onPlayable = () => {
      this.markReady();
      this.cdr.markForCheck();
    };

    videoEl.addEventListener('playing', onPlaying);
    videoEl.addEventListener('pause', onPause);
    videoEl.addEventListener('durationchange', onDurationChange);
    videoEl.addEventListener('loadedmetadata', onDurationChange);
    videoEl.addEventListener('loadeddata', onPlayable);
    videoEl.addEventListener('canplay', onPlayable);

    this.videoEventCleanup.push(
      () => videoEl.removeEventListener('playing', onPlaying),
      () => videoEl.removeEventListener('pause', onPause),
      () => videoEl.removeEventListener('durationchange', onDurationChange),
      () => videoEl.removeEventListener('loadedmetadata', onDurationChange),
      () => videoEl.removeEventListener('loadeddata', onPlayable),
      () => videoEl.removeEventListener('canplay', onPlayable),
    );
  }

  private unbindVideoElementEvents(): void {
    this.videoEventCleanup.splice(0).forEach((cleanup) => cleanup());
    this.boundVideoElement = null;
  }

  private handleDestroyedPlayerError(error: unknown, player: any): boolean {
    if (!this.isDestroyedPlayerError(error)) return false;
    if (this.playerInstance() === player) {
      this.videoPlaying = false;
      this.videoMuted = true;
      this.endPlaybackStartWait();
    }
    this.stopPolling();
    this.cdr.markForCheck();
    return true;
  }

  private isDestroyedPlayerError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const e = error as { code?: unknown; name?: unknown; message?: unknown };
    const code = Number(e.code);
    const name = String(e.name ?? '');
    const message = String(e.message ?? '');
    return code === 1001
      || name === 'API_NOT_AVAILABLE'
      || message.includes('already been destroyed');
  }

  private triggerReplaySpin(): void {
    this.replaySpin = true;
    if (this.spinTimer) clearTimeout(this.spinTimer);
    this.spinTimer = setTimeout(() => {
      this.replaySpin = false;
      this.spinTimer = null;
      this.cdr.markForCheck();
    }, 600);
  }

  // --- Private: Drag logic ---

  private startDrag(clientX: number, rail: HTMLElement): void {
    if (this.controlsDisabled) return;
    this.wasPlayingBeforeDrag = this.videoPlaying;
    if (this.videoPlaying) {
      this.playerInstance()?.pause?.();
    }
    this.dragging = true;
    this.dragged = false;
    this.lastPreviewTs = 0;
    this.progressRailEl = rail;
    this.updateDragRatio(clientX);
    this.dragStartRatio = this.dragRatio;
    this.cdr.markForCheck();
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.dragging) return;
    this.handleDragMove(event.clientX);
  }

  private onPointerUp(event: PointerEvent): void {
    if (!this.dragging) return;
    event.preventDefault();
    event.stopPropagation();
    this.finishDrag(event.clientX);
  }

  private onTouchMove(event: TouchEvent): void {
    if (!this.dragging) return;
    const touch = this.getActiveTouch(event);
    if (!touch) return;
    event.preventDefault();
    this.handleDragMove(touch.clientX);
  }

  private onTouchEnd(event: TouchEvent): void {
    if (!this.dragging) return;
    const touch = this.getActiveTouch(event);
    if (!touch) return;
    event.preventDefault();
    event.stopPropagation();
    this.finishDrag(touch.clientX);
  }

  private handleDragMove(clientX: number): void {
    this.updateDragRatio(clientX);
    if (Math.abs(this.dragRatio - this.dragStartRatio) > this.dragThreshold) {
      this.dragged = true;
    }
    this.cdr.markForCheck();
    const now = performance.now();
    if (this.dragged && now - this.lastPreviewTs >= this.previewThrottleMs) {
      const duration = this.videoDuration || 0;
      const next = Math.min(this.dragRatio * duration, duration);
      this.playerInstance()?.seek?.(next);
      this.lastPreviewTs = now;
    }
  }

  private finishDrag(clientX: number): void {
    this.updateDragRatio(clientX);
    this.detachDragListeners();
    const duration = this.videoDuration || 0;
    let next = Math.min(this.dragRatio * duration, duration);
    if (duration > 0 && this.dragRatio >= 0.995) next = duration;
    this.progressRailEl = undefined;
    // Update local time BEFORE clearing dragging flag to prevent bar jump-back
    this.videoCurrentTime = next;
    this.dragging = false;
    this.dragged = false;
    this.seekingUntil = performance.now() + 300;
    // Always seek — both click-to-seek and drag-to-seek
    const player = this.playerInstance();
    player?.seek?.(next);
    if (this.wasPlayingBeforeDrag && player) {
      this.beginPlaybackStartWait();
      this.requestPlay(player);
      this.seekingUntil = performance.now() + 500;
    }
    this.wasPlayingBeforeDrag = false;
    this.activeTouchId = null;
    this.cdr.markForCheck();
  }

  private updateDragRatio(clientX: number): void {
    const target = this.progressRailEl;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    let ratio = (clientX - rect.left) / rect.width;
    ratio = Math.min(Math.max(ratio, 0), 1);
    if (ratio > 0.995) ratio = 1;
    else if (ratio < 0.005) ratio = 0;
    this.dragRatio = ratio;
  }

  private detachDragListeners(): void {
    window.removeEventListener('pointermove', this.boundMove);
    window.removeEventListener('pointerup', this.boundUp);
    window.removeEventListener('pointercancel', this.boundUp);
    window.removeEventListener('touchmove', this.boundTouchMove);
    window.removeEventListener('touchend', this.boundTouchEnd);
    window.removeEventListener('touchcancel', this.boundTouchEnd);
  }

  private getActiveTouch(event: TouchEvent): Touch | null {
    if (this.activeTouchId === null) return event.changedTouches?.[0] ?? null;
    for (let i = 0; i < event.changedTouches.length; i++) {
      const touch = event.changedTouches.item(i);
      if (touch && touch.identifier === this.activeTouchId) return touch;
    }
    return null;
  }

  /** 再生可能を観測したらラッチ（冪等・片方向）。プレイヤー参照変更時のみ reset される。 */
  private markReady(): void {
    if (this.readyLatched) return;
    this.readyLatched = true;
    this.cdr.markForCheck();
  }

  private beginPlaybackStartWait(): void {
    this.playRequestPending = true;
    const gen = this.playerGeneration;

    // 視覚スピナーはデバウンス後にのみ表示 → 高速起動ではちらつかない。
    if (this.playStartDebounceTimer) clearTimeout(this.playStartDebounceTimer);
    this.playStartDebounceTimer = setTimeout(() => {
      this.playStartDebounceTimer = null;
      if (gen !== this.playerGeneration || !this.playRequestPending) return;
      this.showPlaybackStarting = true;
      this.cdr.markForCheck();
    }, this.playStartDebounceMs);

    // 再生要求時点から計測するハードタイムアウト → 起動できなければ ready/paused に戻す。
    if (this.playStartTimer) clearTimeout(this.playStartTimer);
    this.playStartTimer = setTimeout(() => {
      this.playStartTimer = null;
      if (gen !== this.playerGeneration) return;
      this.endPlaybackStartWait();
    }, this.playStartTimeoutMs);
  }

  private endPlaybackStartWait(): void {
    if (this.playStartDebounceTimer) {
      clearTimeout(this.playStartDebounceTimer);
      this.playStartDebounceTimer = null;
    }
    if (this.playStartTimer) {
      clearTimeout(this.playStartTimer);
      this.playStartTimer = null;
    }
    const changed = this.playRequestPending || this.showPlaybackStarting;
    this.playRequestPending = false;
    this.showPlaybackStarting = false;
    if (changed) this.cdr.markForCheck();
  }

  private resetPlaybackViewState(): void {
    this.endPlaybackStartWait();
    this.unbindVideoElementEvents();
    this.readyLatched = false;
    this.videoPlaying = false;
    this.videoMuted = true;
    this.videoCurrentTime = 0;
    this.videoDuration = 0;
    this.dragging = false;
    this.wasEnded = false;
    this.hasPlaybackSession = false;
    this.thumbnailLoadFailed = false;
    this.seekingUntil = 0;
  }
}
